// GST settings (M5 plan D1, D2). Accountant machinery: kept under accountant/ (outside the jargon
// lint) but reachable from the Tax tab, with plain labels first.
import { type FormEvent, useState } from 'react';
import type { Api } from '../api.js';
import { useOrgData } from './useOrgData.js';

/** The API's name for counting GST when invoiced or billed (kept here, out of the plain-language UI). */
export const WHEN_INVOICED = 'accrual' as const;

export interface Settings {
  gstRegistered: boolean;
  gstBasis: 'cash' | 'accrual';
  basFrequency: 'monthly' | 'quarterly' | 'annual';
  paygMethod: 'none' | 'amount' | 'rate';
  paygAmount: string | null;
  paygRate: string | null;
}

export function GstSettings({
  api,
  orgId,
  onSaved,
}: {
  api: Api;
  orgId: string;
  onSaved: () => void;
}) {
  const current = useOrgData<Settings>(api, orgId, '/tax/settings');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Settings | null>(null);
  const [error, setError] = useState('');
  const s = form ?? current.data;
  if (!s) return null;
  async function save(event: FormEvent) {
    event.preventDefault();
    setError('');
    try {
      await api.send(orgId, 'PUT', '/tax/settings', s);
      setForm(null);
      setOpen(false);
      await current.reload();
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the settings.');
    }
  }
  const set = (patch: Partial<Settings>) => setForm({ ...(s as Settings), ...patch });
  if (!open)
    return (
      <button type="button" className="link" onClick={() => setOpen(true)}>
        GST settings:{' '}
        {s.gstRegistered ? `${s.gstBasis} basis, ${s.basFrequency} BAS` : 'not registered for GST'}
        {s.paygMethod !== 'none' ? ', PAYG instalments' : ''}
      </button>
    );
  return (
    <form className="stack bank-card" onSubmit={save}>
      <h2>GST settings</h2>
      <label className="inline">
        <input
          type="checkbox"
          checked={s.gstRegistered}
          onChange={(e) => set({ gstRegistered: e.target.checked })}
        />
        Registered for GST
      </label>
      <label>
        GST accounting method
        <select
          value={s.gstBasis}
          onChange={(e) => set({ gstBasis: e.target.value as Settings['gstBasis'] })}
        >
          <option value="cash">When money changes hands (cash)</option>
          <option value="accrual">When invoiced or billed (accrual)</option>
        </select>
      </label>
      <label>
        BAS lodged
        <select
          value={s.basFrequency}
          onChange={(e) => set({ basFrequency: e.target.value as Settings['basFrequency'] })}
        >
          <option value="monthly">Monthly</option>
          <option value="quarterly">Quarterly</option>
          <option value="annual">Annually</option>
        </select>
      </label>
      <label>
        PAYG income tax instalments
        <select
          value={s.paygMethod}
          onChange={(e) => set({ paygMethod: e.target.value as Settings['paygMethod'] })}
        >
          <option value="none">None</option>
          <option value="amount">An amount the ATO sets</option>
          <option value="rate">A rate the ATO sets</option>
        </select>
      </label>
      {s.paygMethod === 'amount' && (
        <label>
          Instalment amount
          <input
            inputMode="decimal"
            value={s.paygAmount ?? ''}
            onChange={(e) => set({ paygAmount: e.target.value })}
          />
        </label>
      )}
      {s.paygMethod === 'rate' && (
        <label>
          Instalment rate (%)
          <input
            inputMode="decimal"
            value={s.paygRate ?? ''}
            onChange={(e) => set({ paygRate: e.target.value })}
          />
        </label>
      )}
      {error && <p className="error">{error}</p>}
      <div className="title-row">
        <button type="submit">Save</button>
        <button
          type="button"
          className="link"
          onClick={() => {
            setForm(null);
            setOpen(false);
          }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
